import { useCallback } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { match, P } from "ts-pattern";

import Modal from "@gx/dls/modal/Modal";
import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import {
  PARAM_TO_SOURCE_TYPE_MAP,
  SOURCE_TYPE_TO_PARAM_MAP,
} from "@/pages/sources/components/create/constants";
import CreateSourceSelector from "@/pages/sources/components/create/select/CreateSourceSelector";
import CreateSourceBrowserbase from "@/pages/sources/components/create/sources/browserbase/CreateSourceBrowserbase";
import CreateSourceFileUpload from "@/pages/sources/components/create/sources/file-upload/CreateSourceFileUpload";
import CreateSourceText from "@/pages/sources/components/create/sources/text/CreateSourceText";
import CreateSourceWebhook from "@/pages/sources/components/create/sources/webhook/CreateSourceWebhook";
import CreateSourceWikipedia from "@/pages/sources/components/create/sources/wikipedia/CreateSourceWikipedia";
import { CreateSourceModalProps } from "@/pages/sources/components/create/types";

const CreateSourceModal = ({ onClose }: CreateSourceModalProps) => {
  const { createSource, createSourceType } = useSearch({ from: "__root__" });
  const navigate = useNavigate();

  const sourceType = createSourceType ? PARAM_TO_SOURCE_TYPE_MAP[createSourceType] : undefined;

  const handleClose = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        createSource: undefined,
        createSourceType: undefined,
      }),
    });
    onClose?.();
  }, [navigate, onClose]);

  const handleBack = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        createSource: true,
        createSourceType: undefined,
      }),
    });
  }, [navigate]);

  const handleSelectSourceType = useCallback(
    (sourceType: SourceType) => {
      const param = SOURCE_TYPE_TO_PARAM_MAP[sourceType];
      if (param) {
        void navigate({
          to: ".",
          search: (prev) => ({
            ...prev,
            createSourceType: param,
          }),
        });
      }
    },
    [navigate],
  );

  const isOpen = createSource === true;

  const content = (() => {
    if (sourceType === undefined) {
      return <CreateSourceSelector onClose={handleClose} onSelect={handleSelectSourceType} />;
    }

    return match(sourceType)
      .returnType<React.ReactNode>()
      .with(SourceType.FILE_UPLOAD, () => (
        <CreateSourceFileUpload onClose={handleClose} onBack={handleBack} />
      ))
      .with(SourceType.WEBHOOK, () => (
        <CreateSourceWebhook onClose={handleClose} onBack={handleBack} />
      ))
      .with(SourceType.TEXT, () => <CreateSourceText onClose={handleClose} onBack={handleBack} />)
      .with(SourceType.BROWSERBASE, () => (
        <CreateSourceBrowserbase onClose={handleClose} onBack={handleBack} />
      ))
      .with(SourceType.WIKIPEDIA, () => (
        <CreateSourceWikipedia onClose={handleClose} onBack={handleBack} />
      ))
      .with(P.number, () => null)
      .exhaustive();
  })();

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {content}
    </Modal>
  );
};

export default CreateSourceModal;
